import { useCallback, useEffect, useRef, useState } from "react";
import type { Bounds, GameCoord } from "@/engine/coords.ts";
import {
  centerOn,
  fitBounds,
  screenToGame,
  screenToWorld,
  zoomAround,
  type Camera,
  type ProjectionConfig,
  type Viewport,
} from "@/engine/projection.ts";

/**
 * Camera state and input handling for the map.
 *
 * All the maths lives in `@/engine/projection`; this hook only owns the
 * mutable state and translates browser events into calls on it.
 *
 * Input rules, chosen for someone who is also flying a helicopter:
 *   - wheel zooms around the cursor, never around the centre
 *   - drag pans with any button, so a right-hand-on-stick grab still works
 *   - pinch zooms around the midpoint of the two fingers
 *   - the camera is clamped so the map can never be lost off screen
 */

export const MIN_SCALE = 0.02;
export const MAX_SCALE = 6;

export interface UseCameraResult {
  readonly camera: Camera;
  readonly viewport: Viewport;
  readonly containerRef: React.RefObject<HTMLDivElement | null>;
  readonly isPanning: boolean;
  /** Game coordinate currently under the pointer, or null when outside. */
  readonly cursor: GameCoord | null;
  // Properties, not methods: these get passed straight to onClick handlers, so
  // they must not carry an implicit `this`.
  readonly fit: (bounds: Bounds) => void;
  readonly focus: (point: GameCoord, scale?: number) => void;
  readonly zoomBy: (factor: number) => void;
}

export function useCamera(config: ProjectionConfig, initialBounds: Bounds): UseCameraResult {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [viewport, setViewport] = useState<Viewport>({ width: 0, height: 0 });
  const [camera, setCamera] = useState<Camera>({ scale: 0.1, x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [cursor, setCursor] = useState<GameCoord | null>(null);

  // Keep the latest values readable from event handlers without re-binding them.
  const cameraRef = useRef(camera);
  cameraRef.current = camera;
  const viewportRef = useRef(viewport);
  viewportRef.current = viewport;
  const configRef = useRef(config);
  configRef.current = config;

  const hasFittedRef = useRef(false);
  const previousViewportRef = useRef<Viewport>({ width: 0, height: 0 });

  // --- viewport tracking -------------------------------------------------

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const { width, height } = entry.contentRect;
      setViewport({ width, height });
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  // --- initial fit, and staying sane across resizes ----------------------

  /**
   * The first real layout fits the map. After that a resize must NOT re-fit,
   * or moving the window would throw away wherever the user had panned to.
   * Instead the world point at the centre of the viewport is held in place,
   * which is what feels right when a window is dragged between monitors or
   * toggled to fullscreen.
   */
  useEffect(() => {
    if (viewport.width === 0 || viewport.height === 0) return;

    if (!hasFittedRef.current) {
      hasFittedRef.current = true;
      previousViewportRef.current = viewport;
      setCamera(fitBounds(initialBounds, viewport, config));
      return;
    }

    const previous = previousViewportRef.current;
    if (previous.width === viewport.width && previous.height === viewport.height) return;
    previousViewportRef.current = viewport;

    setCamera((camera) => {
      const centreWorld = screenToWorld(
        { x: previous.width / 2, y: previous.height / 2 },
        camera,
      );
      return {
        scale: camera.scale,
        x: viewport.width / 2 - centreWorld.x * camera.scale,
        y: viewport.height / 2 - centreWorld.y * camera.scale,
      };
    });
  }, [viewport, initialBounds, config]);

  /** A different map has different bounds, so it always gets a fresh fit. */
  useEffect(() => {
    const current = viewportRef.current;
    if (current.width === 0 || current.height === 0) return;
    setCamera(fitBounds(initialBounds, current, configRef.current));
  }, [initialBounds]);

  const fit = useCallback((bounds: Bounds) => {
    const current = viewportRef.current;
    if (current.width === 0 || current.height === 0) return;
    setCamera(fitBounds(bounds, current, configRef.current));
  }, []);

  const focus = useCallback((point: GameCoord, scale?: number) => {
    const current = viewportRef.current;
    if (current.width === 0 || current.height === 0) return;
    const nextScale = Math.min(
      MAX_SCALE,
      Math.max(MIN_SCALE, scale ?? cameraRef.current.scale),
    );
    setCamera(centerOn(point, nextScale, current, configRef.current));
  }, []);

  const zoomBy = useCallback((factor: number) => {
    const current = viewportRef.current;
    const focusPoint = { x: current.width / 2, y: current.height / 2 };
    setCamera((previous) =>
      zoomAround(previous, focusPoint, previous.scale * factor, MIN_SCALE, MAX_SCALE),
    );
  }, []);

  // --- wheel zoom --------------------------------------------------------

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const onWheel = (event: WheelEvent): void => {
      // Must be non-passive to stop the browser zooming the page instead.
      event.preventDefault();
      const rect = element.getBoundingClientRect();
      const focusPoint = {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      };
      // Normalise line-mode deltas so a mouse wheel and a trackpad feel alike.
      const delta = event.deltaMode === 1 ? event.deltaY * 16 : event.deltaY;
      const factor = Math.exp(-delta * 0.0015);
      setCamera((previous) =>
        zoomAround(previous, focusPoint, previous.scale * factor, MIN_SCALE, MAX_SCALE),
      );
    };

    element.addEventListener("wheel", onWheel, { passive: false });
    return () => element.removeEventListener("wheel", onWheel);
  }, []);

  // --- pointer pan and pinch --------------------------------------------

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const pointers = new Map<number, { x: number; y: number }>();
    let pinchDistance = 0;
    let panned = false;

    const localPoint = (event: PointerEvent): { x: number; y: number } => {
      const rect = element.getBoundingClientRect();
      return { x: event.clientX - rect.left, y: event.clientY - rect.top };
    };

    const onPointerDown = (event: PointerEvent): void => {
      // Something on the map wants this press for itself — a marker being
      // clicked to remove it, say. If we captured the pointer here the click
      // would be redirected to this container and never reach it, so leave
      // that gesture entirely alone.
      const target = event.target;
      if (target instanceof Element && target.closest("[data-no-pan]")) return;

      pointers.set(event.pointerId, localPoint(event));
      element.setPointerCapture(event.pointerId);
      panned = false;
      if (pointers.size === 1) setIsPanning(true);
      if (pointers.size === 2) pinchDistance = currentPinchDistance();
    };

    const currentPinchDistance = (): number => {
      const [a, b] = [...pointers.values()];
      if (!a || !b) return 0;
      return Math.hypot(b.x - a.x, b.y - a.y);
    };

    const pinchMidpoint = (): { x: number; y: number } => {
      const [a, b] = [...pointers.values()];
      if (!a || !b) return { x: 0, y: 0 };
      return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
    };

    const onPointerMove = (event: PointerEvent): void => {
      const point = localPoint(event);

      if (!pointers.has(event.pointerId)) {
        // Hover: just report the coordinate under the cursor.
        setCursor(screenToGame(point, cameraRef.current, configRef.current));
        return;
      }

      const previous = pointers.get(event.pointerId);
      pointers.set(event.pointerId, point);
      if (!previous) return;

      if (pointers.size === 1) {
        const dx = point.x - previous.x;
        const dy = point.y - previous.y;
        if (Math.abs(dx) > 1 || Math.abs(dy) > 1) panned = true;
        setCamera((camera) => ({ ...camera, x: camera.x + dx, y: camera.y + dy }));
        return;
      }

      if (pointers.size === 2) {
        panned = true;
        const distance = currentPinchDistance();
        if (pinchDistance > 0 && distance > 0) {
          const factor = distance / pinchDistance;
          const midpoint = pinchMidpoint();
          setCamera((camera) =>
            zoomAround(camera, midpoint, camera.scale * factor, MIN_SCALE, MAX_SCALE),
          );
        }
        pinchDistance = distance;
      }
    };

    const endPointer = (event: PointerEvent): void => {
      pointers.delete(event.pointerId);
      if (element.hasPointerCapture(event.pointerId)) {
        element.releasePointerCapture(event.pointerId);
      }
      if (pointers.size < 2) pinchDistance = 0;
      if (pointers.size === 0) {
        setIsPanning(false);
        // Mark the gesture so a click handler can tell a tap from a drag.
        if (panned) element.dataset["panned"] = "true";
        else delete element.dataset["panned"];
      }
    };

    const onPointerLeave = (): void => setCursor(null);

    element.addEventListener("pointerdown", onPointerDown);
    element.addEventListener("pointermove", onPointerMove);
    element.addEventListener("pointerup", endPointer);
    element.addEventListener("pointercancel", endPointer);
    element.addEventListener("pointerleave", onPointerLeave);

    return () => {
      element.removeEventListener("pointerdown", onPointerDown);
      element.removeEventListener("pointermove", onPointerMove);
      element.removeEventListener("pointerup", endPointer);
      element.removeEventListener("pointercancel", endPointer);
      element.removeEventListener("pointerleave", onPointerLeave);
    };
  }, []);

  return { camera, viewport, containerRef, isPanning, cursor, fit, focus, zoomBy };
}
